import { useEffect, useMemo, useState } from 'react'
import { MapContainer, TileLayer, Marker, Polyline, Polygon, useMap } from 'react-leaflet'
import L from 'leaflet'
import BaseMapSwitcher from './BaseMapSwitcher'
import { BASEMAPS } from '../utils/basemaps'
import { useBaseMap } from '../context/BaseMapContext'

type LatLng = [number, number]

export default function GeoJSONViewerModal({ text, onClose }: { text: string; onClose: () => void }) {
  const data = useMemo(() => parseGeoJSON(text), [text])
  const { baseId, setBaseId } = useBaseMap()
  const [focus, setFocus] = useState<LatLng | null>(null)
  return (
    <div className="modal-overlay">
      <div className="modal-container" style={{ width: '96vw', maxWidth: '1400px' }}>
        <div className="modal-header">
          <h3>GeoJSON Preview</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 360px', gap: '1rem', alignItems: 'stretch' }}>
            <div style={{ height: '80vh', width: '100%' }}>
              <MapContainer center={data.center || [62, 15]} zoom={data.center ? 12 : 5} style={{ height: '100%', width: '100%', borderRadius: 12, boxShadow: '0 10px 30px rgba(0,0,0,0.08)' }}>
                <TileLayer key={baseId} url={BASEMAPS[baseId].url} attribution={BASEMAPS[baseId].attribution} {...(BASEMAPS[baseId].options || {})} />
                {data.bounds && <FitToBounds bounds={data.bounds} />}
                {focus && <FocusTo target={focus} />}
                {data.points.map((p, i) => (
                  <Marker key={`pt-${i}`} position={p.pos} icon={typeIcon(p.assetType, p.name)} />
                ))}
                {data.lines.map((line, i) => (
                  <Polyline key={`ln-${i}`} positions={line} pathOptions={{ color: '#0ea5e9', weight: 3 }} />
                ))}
                {data.polygons.map((poly, i) => (
                  <Polygon key={`pg-${i}`} positions={poly} pathOptions={{ color: '#f59e0b', weight: 2, fillOpacity: 0.2 }} />
                ))}
              </MapContainer>
            </div>
            <div style={{ height: '80vh', overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0.75rem', borderBottom: '1px solid #f3f4f6' }}>
                <div style={{ color: '#374151', fontWeight: 600 }}>Features</div>
                <BaseMapSwitcher value={baseId} onChange={setBaseId} />
              </div>
              <div>
                {data.items.length === 0 ? (
                  <div style={{ padding: '0.75rem', color: '#6b7280' }}>No features</div>
                ) : (
                  data.items.map((it, i) => (
                    <button key={`it-${i}`} className="btn" style={{ display: 'flex', width: '100%', justifyContent: 'flex-start', gap: '0.5rem', borderRadius: 0, borderBottom: '1px solid #f3f4f6' }} onClick={() => it.center && setFocus(it.center)}>
                      <span style={{ fontSize: 18 }}>{typeIconEmoji(it.assetType)}</span>
                      <span style={{ color: '#111827' }}>{it.label}</span>
                      {it.center && (<span style={{ color: '#6b7280', marginLeft: 'auto', fontSize: 12 }}>{it.center[0].toFixed(5)}, {it.center[1].toFixed(5)}</span>)}
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function FitToBounds({ bounds }: { bounds: L.LatLngBoundsExpression }) {
  const map = useMap()
  useEffect(() => {
    try { map.fitBounds(bounds, { paddingTopLeft: L.point(30, 30), paddingBottomRight: L.point(30, 30) }) } catch {}
  }, [bounds, map])
  return null
}

function FocusTo({ target }: { target: LatLng }) {
  const map = useMap()
  useEffect(() => {
    try { map.setView(target as L.LatLngExpression, Math.max(map.getZoom(), 14)) } catch {}
  }, [target, map])
  return null
}

function typeIcon(type?: string, label?: string) {
  const iconMap: Record<string, string> = {
    hunting_border: '🟫', pass: '🚶', tower: '🗼', burrow: '🕳️', salt_lick: '🧂', food_place: '🥣', trap: '🪤',
    bridge: '🪵', parking: '🅿️', ford: '🌊'
  }
  const known = type && iconMap[type]
  const emoji = known || '⚠️'
  const title = known ? '' : (type ? `Unknown type: ${safe(type)}` : 'Unknown type')
  return L.divIcon({
    html: `
      <div class="gpx-marker" title="${title}">
        <div class="gpx-emoji">${emoji}</div>
        ${label ? `<div class="gpx-label">${safe(label)}</div>` : ''}
      </div>
    `,
    className: 'gpx-marker-icon',
    iconSize: [28, 40],
    iconAnchor: [14, 14]
  })
}

function typeIconEmoji(type?: string) {
  const iconMap: Record<string, string> = {
    hunting_border: '🟫', pass: '🚶', tower: '🗼', burrow: '🕳️', salt_lick: '🧂', food_place: '🥣', trap: '🪤',
    bridge: '🪵', parking: '🅿️', ford: '🌊'
  }
  return (type && iconMap[type]) || '⚠️'
}

function safe(s: string) {
  return s.replace(/[&<>"']/g, (c) => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' } as Record<string, string>)[c])
}

function parseGeoJSON(text: string): {
  items: { label: string; center?: LatLng; assetType?: string }[];
  points: { pos: LatLng; name?: string; assetType?: string }[];
  lines: LatLng[][];
  polygons: LatLng[][][];
  bounds?: L.LatLngBoundsExpression;
  center?: LatLng
} {
  try {
    const gj = JSON.parse(text)
    const points: { pos: LatLng; name?: string; assetType?: string }[] = []
    const lines: LatLng[][] = []
    const polygons: LatLng[][][] = []
    const items: { label: string; center?: LatLng; assetType?: string }[] = []

    function addPoint(coords: any, props?: any) {
      if (!Array.isArray(coords) || coords.length < 2) return
      const pos: LatLng = [coords[1], coords[0]]
      const name = props?.name || props?.title
      const assetType = props?.type || props?.assetType
      points.push({ pos, name, assetType })
      items.push({ label: name || (assetType ? assetType : 'Point'), center: pos, assetType })
    }
    function addLine(coords: any, props?: any) {
      if (!Array.isArray(coords)) return
      const line: LatLng[] = coords.map((c: any) => [c[1], c[0]])
      if (line.length) {
        lines.push(line)
        const mid = line[Math.floor(line.length/2)]
        items.push({ label: (props?.name || 'LineString'), center: mid, assetType: props?.type || props?.assetType })
      }
    }
    function addPolygon(coords: any, props?: any) {
      if (!Array.isArray(coords)) return
      const rings: LatLng[][] = coords.map((ring: any) => ring.map((c: any) => [c[1], c[0]]))
      if (rings.length) {
        polygons.push(rings)
        const r = rings[0]; const mid = r[Math.floor(r.length/2)]
        items.push({ label: (props?.name || 'Polygon'), center: mid, assetType: props?.type || props?.assetType })
      }
    }

    function handleGeometry(geom: any, props?: any) {
      const t = (geom?.type || '').toUpperCase()
      const c = geom?.coordinates
      switch (t) {
        case 'POINT': addPoint(c, props); break
        case 'MULTIPOINT': Array.isArray(c) && c.forEach((p: any) => addPoint(p, props)); break
        case 'LINESTRING': addLine(c, props); break
        case 'MULTILINESTRING': Array.isArray(c) && c.forEach((ls: any) => addLine(ls, props)); break
        case 'POLYGON': addPolygon(c, props); break
        case 'MULTIPOLYGON': Array.isArray(c) && c.forEach((pg: any) => addPolygon(pg, props)); break
        default:
          break
      }
    }

    if (gj.type === 'FeatureCollection' && Array.isArray(gj.features)) {
      gj.features.forEach((f: any) => handleGeometry(f.geometry, f.properties))
    } else if (gj.type === 'Feature') {
      handleGeometry(gj.geometry, gj.properties)
    } else {
      handleGeometry(gj, gj.properties)
    }

    const all = [...points.map(p=>p.pos), ...lines.flat(), ...polygons.flat(2)]
    let bounds: L.LatLngBoundsExpression | undefined
    let center: LatLng | undefined
    if (all.length) {
      let minLat=90,minLon=180,maxLat=-90,maxLon=-180
      all.forEach(([lat, lon])=>{ if(lat<minLat)minLat=lat; if(lon<minLon)minLon=lon; if(lat>maxLat)maxLat=lat; if(lon>maxLon)maxLon=lon })
      bounds = [[minLat,minLon],[maxLat,maxLon]]
      center = [(minLat+maxLat)/2,(minLon+maxLon)/2]
    }
    return { items, points, lines, polygons, bounds, center }
  } catch {
    return { items: [], points: [], lines: [], polygons: [] }
  }
}
